// <weather-station-picker> — progressive enhancement of the
// "nearest Wunderground weather station" dropdown on the Site form.
//
// Without JS: the bare <select> inside .weather-station-fallback is the picker.
// With JS: a small <map-box> with one marker per station appears;
//          clicking a marker mutates the (now-hidden) <select>.
//
// Markup the host renders:
//
//   <weather-station-picker eventbus
//       data-site-lat="…" data-site-lon="…"
//       data-home-icon="…"     <!-- URL of site.svg -->
//       data-station-icon="…"> <!-- URL of weather-station.svg -->
//     <div class="weather-station-fallback">
//       <select name="...">
//         <option value="WUID-1" data-lat="..." data-lon="...">…</option>
//         …
//       </select>
//     </div>
//   </weather-station-picker>

import crelt from 'crelt';

// Marker styles must live inside <map-box>'s shadow DOM, because OL Overlay
// re-parents each <site-marker> into the map's viewport (which lives in
// the shadow tree). Light-DOM CSS doesn't cross the boundary.
const markerStyleSheet = new CSSStyleSheet();
markerStyleSheet.replaceSync(`
  .weather-station-marker {
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
    position: relative;
    border-radius: 50%;
    transition: transform 120ms ease;
  }
  .weather-station-marker img {
    display: block;
    border-radius: 50%;
  }
  .weather-station-marker:hover,
  .weather-station-marker:focus-visible {
    transform: scale(1.1);
  }
  .weather-station-marker::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 3px solid transparent;
    pointer-events: none;
    transition: border-color 120ms ease, box-shadow 120ms ease;
  }
  .weather-station-marker.active {
    transform: scale(1.25);
    z-index: 1;
  }
  .weather-station-marker.active::after {
    border-color: #f5a623;
    box-shadow: 0 0 0 2px rgba(245, 166, 35, 0.45);
  }
  .weather-station-home-marker {
    display: block;
    pointer-events: none;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4));
  }
`);


export class WeatherStationPicker extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this._connected) return;
    this._connected = true;

    this.select = this.querySelector('select');
    if (!this.select) return;  // API-failure fallback (free-text input only)

    const stations = Array.from(this.select.options)
        .map(opt => ({
          wuid:  opt.value,
          label: opt.textContent.trim(),
          lat:   parseFloat(opt.dataset.lat),
          lon:   parseFloat(opt.dataset.lon),
        }))
        .filter(s => s.wuid && !isNaN(s.lat) && !isNaN(s.lon));
    if (stations.length === 0) return;

    const siteLat = parseFloat(this.dataset.siteLat);
    const siteLon = parseFloat(this.dataset.siteLon);
    const hasSite = !isNaN(siteLat) && !isNaN(siteLon);
    const stationIcon = this.dataset.stationIcon;
    const homeIcon    = this.dataset.homeIcon;

    const mapBox = crelt('map-box', {
      'center-lat': hasSite ? this.dataset.siteLat : stations[0].lat,
      'center-lon': hasSite ? this.dataset.siteLon : stations[0].lon,
    });

    if (hasSite && homeIcon) {
      mapBox.appendChild(this.buildHomeMarker(siteLat, siteLon, homeIcon));
    }

    this.markerButtons = [];
    stations.forEach(s => {
      mapBox.appendChild(this.buildStationMarker(s, stationIcon));
    });

    this.appendChild(mapBox);

    // Inject marker styles into the map's shadow DOM (where OL re-parents
    // the marker elements). map-box runs attachShadow() synchronously in
    // its connectedCallback, so shadowRoot is set right after appendChild.
    if (mapBox.shadowRoot && !mapBox.shadowRoot.adoptedStyleSheets.includes(markerStyleSheet)) {
      mapBox.shadowRoot.adoptedStyleSheets.push(markerStyleSheet);
    }

    this.dataset.enhanced = 'true';

    this.updateActiveMarker(this.select.value);
  }

  buildHomeMarker(lat, lon, iconUrl) {
    const homeTitle = window.I18n.t('js.weather_station_picker.home_title');
    const marker = crelt('site-marker', {
      slot: 'markers',
      id:   'ws-picker-home',
      title: homeTitle,
      lat:  String(lat),
      lon:  String(lon),
    });
    marker.appendChild(crelt('img', {
      class: 'weather-station-home-marker',
      src:   iconUrl,
      alt:   homeTitle,
      width:  '36',
      height: '36',
    }));
    return marker;
  }

  buildStationMarker(station, iconUrl) {
    const marker = crelt('site-marker', {
      slot: 'markers',
      id:   `ws-picker-${station.wuid}`,
      title: station.label,
      lat:  String(station.lat),
      lon:  String(station.lon),
    });
    const button = crelt('button', {
      type:  'button',
      class: 'weather-station-marker',
      title: station.label,
      'data-wuid': station.wuid,
      'aria-pressed': 'false',
    });
    if (iconUrl) {
      button.appendChild(crelt('img', {
        src: iconUrl,
        alt: '',
        width:  '32',
        height: '32',
      }));
    } else {
      button.textContent = window.I18n.t('js.weather_station_picker.placeholder_icon');
    }
    button.addEventListener('click', e => {
      e.preventDefault();
      this.selectStation(station.wuid);
    });
    marker.appendChild(button);
    this.markerButtons.push(button);
    return marker;
  }

  selectStation(wuid) {
    if (!this.select) return;
    this.select.value = wuid;
    this.select.dispatchEvent(new Event('change', {bubbles: true}));
    this.updateActiveMarker(wuid);
  }

  updateActiveMarker(wuid) {
    if (!this.markerButtons) return;
    this.markerButtons.forEach(btn => {
      const active = btn.dataset.wuid === wuid;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }
}
